import * as React from 'react';

import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiResource, readAPICache } from '../../state';
import { UserAvatar } from '../Users';

/** Possible values for the filtering state */
export enum CommentStatusFilters {
    Open = 'open',
    Resolved = 'resolved',
}

export enum CommentPageFilters {
    All = 'all',
    Page = 'page',
}

/**
 * Button to choose how to filter comments.
 */
export function FilterCommentsButton(props: {
    spaceId: string;
    changeRequestId: string | undefined;
    statusFilter: CommentStatusFilters;
    setStatusFilter: (status: CommentStatusFilters) => void;
    authorsFilter: Set<string>;
    setAuthorsFilter: (authors: Set<string>) => void;
}) {
    const {
        spaceId,
        changeRequestId,
        statusFilter,
        setStatusFilter,
        authorsFilter,
        setAuthorsFilter,
    } = props;

    const filterButtonRef = React.useRef<HTMLButtonElement>(null);

    const hasFilteredByUser = authorsFilter.size > 0;

    const isFiltered = statusFilter !== CommentStatusFilters.Open || authorsFilter.size > 0;

    const filterPalette = () => {
        return createPalette({
            position: {
                preferredPlacement: 'bottom',
                width: PalettePositionWidth.SMALL,
            },
            groups: [
                {
                    key: 'status',
                    title: 'Status',
                    commands: [
                        {
                            key: 'open',
                            title: 'Open',
                            icon: Icon.Comment,
                            trigger: () => {
                                setStatusFilter(CommentStatusFilters.Open);
                            },
                            type: PaletteCommandType.RADIO as const,
                            selected: statusFilter === CommentStatusFilters.Open,
                        },
                        {
                            key: 'resolved',
                            title: 'Resolved',
                            icon: Icon.CommentResolve,
                            trigger: () => {
                                setStatusFilter(CommentStatusFilters.Resolved);
                            },
                            type: PaletteCommandType.RADIO as const,
                            selected: statusFilter === CommentStatusFilters.Resolved,
                        },
                    ],
                },
                {
                    key: 'users-submenu',
                    title: 'Author',
                    commands: () => {
                        const commenters = readAPICache(
                            changeRequestId
                                ? apiResource('listCommentersInChangeRequest', {
                                      spaceId,
                                      changeRequestId,
                                  })
                                : apiResource('listCommentersInSpace', { spaceId })
                        );

                        return [
                            ...commenters.items.map(({ user }) => ({
                                key: `user-${user.id}`,
                                icon: <UserAvatar user={user} size="xs" />,
                                title: user.displayName,
                                trigger: () => {
                                    const newAuthors = new Set(authorsFilter);
                                    if (authorsFilter.has(user.id)) {
                                        newAuthors.delete(user.id);
                                    } else {
                                        newAuthors.add(user.id);
                                    }
                                    setAuthorsFilter(newAuthors);
                                },
                                type: PaletteCommandType.CHECKBOX as const,
                                selected: authorsFilter.has(user.id),
                            })),

                            hasFilteredByUser
                                ? {
                                      key: 'clear',
                                      title: `Clear ${authorsFilter.size > 1 ? 'authors' : 'author'}`,
                                      icon: Icon.X,
                                      trigger: () => {
                                          setAuthorsFilter(new Set());
                                      },
                                  }
                                : null,
                        ].filter(filterOutNullable);
                    },
                },
            ],
        });
    };

    return (
        <PaletteButton
            ref={filterButtonRef}
            palette={filterPalette}
            kind="blank"
            size="small"
            label="Filter comments"
            iconOnly
            leadingIcon={Icon.Filter}
            dot={isFiltered ? 'active' : undefined}
        />
    );
}
